import * as PopoverPrimitive from "@radix-ui/react-popover";
import { ChevronDown, Gift, ImageIcon } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { cx } from "@/components/ui";
import { t } from "@/lib/i18n";

export interface PreviewLine { sku: string; name: string; quantity: number; insert?: boolean; imageUrl?: string | null }

/**
 * „2 Artikel ⌄“ in der Auftragsliste: Positionen mit Bild, Name, SKU und Menge – öffnet beim Drüberfahren
 * (kurz verzögert) und per Klick; der Klick öffnet nicht den Auftrag.
 */
export function ItemsPopover({ label, lines, more, header }: { label: string; lines: PreviewLine[]; more: boolean; header: ReactNode }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const later = (v: boolean, ms: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(v), ms);
  };
  const hover = { onMouseEnter: () => later(true, 250), onMouseLeave: () => later(false, 150) };
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          {...hover}
          onClick={(e) => { e.stopPropagation(); if (timer.current) clearTimeout(timer.current); setOpen((o) => !o); }}
          className={cx("inline-flex items-center gap-0.5 rounded-md px-1 -mx-1 hover:bg-surface-2 hover:text-fg", open && "bg-surface-2 text-fg")}
        >
          {t(label)}
          <ChevronDown className={cx("size-3 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          {...hover}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          className="z-50 w-[340px] rounded-2xl border border-border bg-surface p-2.5 shadow-xl animate-fade-in"
        >
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="bg-surface-2 px-3.5 py-2.5">{header}</div>
            <ul className="divide-y divide-border">
              {lines.map((l, i) => (
                <li key={i} className="flex items-center gap-3 px-3.5 py-2.5">
                  {l.imageUrl ? (
                    <img src={l.imageUrl} alt="" loading="lazy" className="size-11 shrink-0 rounded-lg border border-border object-cover bg-surface" />
                  ) : (
                    <span className="size-11 shrink-0 rounded-lg border border-border bg-surface grid place-items-center text-fg-subtle">
                      {l.insert ? <Gift className="size-4" /> : <ImageIcon className="size-4" />}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium truncate" title={l.name}>{l.name}</span>
                    <span className="block text-[12px] text-fg-muted font-mono truncate">{l.sku}{l.insert ? ` · ${t("Beilage")}` : ""}</span>
                  </span>
                  <span className="shrink-0 text-[13px] tabular-nums text-fg-muted">× {l.quantity}</span>
                </li>
              ))}
            </ul>
            {more && <p className="px-3.5 py-2 text-[12px] text-fg-muted border-t border-border">{t("… und weitere – im Auftrag ansehen")}</p>}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
